import { useCallback, useContext, useEffect, useState } from 'react';

import { AxiosResponse } from 'axios';
import { UseFormReturn } from 'react-hook-form';

import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { getStaffRequest } from '@vyce/core/src/api/companies';
import {
  ExpiringDocumentsSettingPayload,
  ExpiringDocumentsSettings,
  ExpiringDocumentsSettingsListResponse,
} from '@vyce/core/src/api/types';

import { TEAMS_RECIPIENT_ROLES } from '../constants';
import {
  ExpiringDocumentsFormValue,
  ExpiringDocumentsFormValues,
  formValueToPayload,
  settingsToFormValue,
} from '../types';

interface Props {
  companyId?: string;
  allTeamIds: string[];
  methods: UseFormReturn<ExpiringDocumentsFormValues>;
  remove: (index: number) => void;
  getSettings: (companyId: string) => Promise<AxiosResponse<ExpiringDocumentsSettingsListResponse>>;
  createSetting: (
    companyId: string,
    payload: ExpiringDocumentsSettingPayload
  ) => Promise<AxiosResponse<ExpiringDocumentsSettings>>;
  updateSetting: (
    companyId: string,
    settingId: string,
    payload: ExpiringDocumentsSettingPayload
  ) => Promise<AxiosResponse<ExpiringDocumentsSettings>>;
  deleteSetting: (companyId: string, settingId: string) => Promise<AxiosResponse<unknown>>;
  getStaff?: typeof getStaffRequest;
}

const isNotFound = (error: any): boolean => error?.response?.status === 404;

export const useExpiringDocumentsSettings = ({
  companyId,
  allTeamIds,
  methods,
  remove,
  getSettings,
  createSetting,
  updateSetting,
  deleteSetting,
  getStaff = getStaffRequest,
}: Props) => {
  const { showNotification, handleServerError } = useContext(NotificationContext);
  const [loading, setLoading] = useState(true);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  const loadSettings = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const { data } = await getSettings(companyId);
      const items = data?.items ?? [];

      const recipientIds = Array.from(
        new Set(items.flatMap(item => item.recipient_user_ids ?? []))
      ).filter(Boolean);

      let recipientIdToName: Record<string, string> = {};
      if (recipientIds.length) {
        try {
          const staffRes = await getStaff(companyId, {
            limit: 1000,
            offset: 0,
            roles: TEAMS_RECIPIENT_ROLES,
          });
          recipientIdToName = staffRes.data.items.reduce<Record<string, string>>((acc, item) => {
            acc[item.user_id] = `${item.first_name} ${item.last_name}`;
            return acc;
          }, {});
        } catch (e) {
          // fall back to showing raw ids if staff lookup fails
        }
      }

      methods.reset({
        notifications: items.map(item => settingsToFormValue(item, allTeamIds, recipientIdToName)),
      });
    } catch (e) {
      methods.reset({ notifications: [] });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const validate = useCallback(
    (form: ExpiringDocumentsFormValue): boolean => {
      if (!form.name.trim()) {
        showNotification({ message: 'Please enter a notification name', options: { variant: 'error' } });
        return false;
      }
      if (form.enabled) {
        if (form.frequency === 'weekly' && !form.dayOfWeek) {
          showNotification({ message: 'Please select a day of the week', options: { variant: 'error' } });
          return false;
        }
        if (form.frequency === 'specific_day' && !form.dayOfMonth) {
          showNotification({ message: 'Please select a day of the month', options: { variant: 'error' } });
          return false;
        }
        if (!form.receivers.length) {
          showNotification({ message: 'Please add at least one recipient', options: { variant: 'error' } });
          return false;
        }
      }
      return true;
    },
    [showNotification]
  );

  const saveNotification = useCallback(
    async (index: number) => {
      if (!companyId) return;
      const form = methods.getValues(`notifications.${index}`);
      if (!form || !validate(form)) return;

      const payload = formValueToPayload(form, allTeamIds.length);
      setSavingIndex(index);
      try {
        if (form.uuid) {
          await updateSetting(companyId, form.uuid, payload);
        } else {
          const { data } = await createSetting(companyId, payload);
          methods.setValue(`notifications.${index}.uuid`, data.uuid);
        }
        showNotification({ message: 'Notification saved', options: { variant: 'success' } });
      } catch (e) {
        if (isNotFound(e)) {
          showNotification({
            message: 'This notification no longer exists. Reloading the list.',
            options: { variant: 'error' },
          });
          await loadSettings();
        } else {
          handleServerError(e);
        }
      } finally {
        setSavingIndex(null);
      }
    },
    [companyId, methods, allTeamIds.length, validate, createSetting, updateSetting, loadSettings, showNotification, handleServerError]
  );

  const deleteNotification = useCallback(
    async (index: number) => {
      const form = methods.getValues(`notifications.${index}`);

      if (!companyId || !form?.uuid) {
        remove(index);
        return;
      }

      setDeletingIndex(index);
      try {
        await deleteSetting(companyId, form.uuid);
        remove(index);
        showNotification({ message: 'Notification deleted', options: { variant: 'success' } });
      } catch (e) {
        if (isNotFound(e)) {
          showNotification({
            message: 'This notification no longer exists. Reloading the list.',
            options: { variant: 'error' },
          });
          await loadSettings();
        } else {
          handleServerError(e);
        }
      } finally {
        setDeletingIndex(null);
      }
    },
    [companyId, methods, remove, deleteSetting, loadSettings, showNotification, handleServerError]
  );

  const toggleNotification = useCallback(
    async (index: number, enabled: boolean) => {
      methods.setValue(`notifications.${index}.enabled`, enabled);

      const form = methods.getValues(`notifications.${index}`);
      // Persist immediately for already-saved notifications; new ones persist on first Save.
      if (!companyId || !form?.uuid) return;

      setSavingIndex(index);
      try {
        await updateSetting(companyId, form.uuid, formValueToPayload({ ...form, enabled }, allTeamIds.length));
      } catch (e) {
        if (isNotFound(e)) {
          showNotification({
            message: 'This notification no longer exists. Reloading the list.',
            options: { variant: 'error' },
          });
          await loadSettings();
        } else {
          methods.setValue(`notifications.${index}.enabled`, !enabled);
          handleServerError(e);
        }
      } finally {
        setSavingIndex(null);
      }
    },
    [companyId, methods, allTeamIds.length, updateSetting, loadSettings, showNotification, handleServerError]
  );

  return { loading, savingIndex, deletingIndex, saveNotification, deleteNotification, toggleNotification };
};
